import { Home, NotebookPen, Bot, Settings, Shield, LogOut, Gift } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { Logo } from './Logo';

export function AppNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('User');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadUserProfile();
  }, []);

  async function loadUserProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, full_name, role, email')
        .eq('id', user.id)
        .maybeSingle();

      if (profile) {
        setUserName(profile.display_name || profile.full_name || 'User');
        setIsAdmin(profile.role === 'admin');

        if (profile.email === 'montano248@gmail.com' && profile.role !== 'admin') {
          await supabase
            .from('profiles')
            .update({ role: 'admin' })
            .eq('id', user.id);
          setIsAdmin(true);
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  const navItems = [
    { icon: Home, label: 'Today', path: '/app' },
    { icon: NotebookPen, label: 'Journal', path: '/journal' },
    { icon: Bot, label: 'Coach', path: '/coach' },
    { icon: Settings, label: 'Settings', path: '/settings' }
  ];

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/app" className="flex items-center mr-8">
              <Logo className="h-8 w-8" />
            </Link>
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-gold-500 text-ink-950 shadow-sm'
                        : 'text-gray-700 hover:bg-gold-500/10 hover:text-gold-500'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Link
              to="/invite"
              className="inline-flex items-center px-4 py-2 text-sm font-medium bg-gradient-to-r from-gold-400/20 to-amber-400/20 text-gold-600 hover:from-gold-400/30 hover:to-amber-400/30 rounded-lg transition-all duration-200 group border border-gold-200"
              title="Invite friends and earn free months"
            >
              <Gift className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              <span className="hidden sm:inline">Invite</span>
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="inline-flex items-center px-4 py-2 text-sm font-medium bg-gold-500/10 text-gold-500 hover:bg-gold-500/20 rounded-lg transition-all duration-200 group"
              >
                <Shield className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Admin Panel</span>
                <span className="sm:hidden">Admin</span>
              </Link>
            )}
            <div className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              <div className="w-9 h-9 bg-gold-500 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-ink-950 font-bold text-sm">
                  {userName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500">Member</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center justify-center p-2 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 group"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-t border-gray-200">
        <div className="flex overflow-x-auto space-x-1 px-4 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`inline-flex flex-col items-center justify-center min-w-[4rem] px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? 'bg-gold-500 text-ink-950'
                    : 'text-gray-700 hover:bg-gold-500/10 hover:text-gold-500'
                }`}
              >
                <Icon className="h-5 w-5 mb-1" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
